import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Input } from 'antd'
import {
  useAppSelector,
  useAppDispatch,
  useVip,
  useSensorsRole
} from '../../../../app/hooks'
import {
  setEditorGeoItemList,
  setEditorWatermark
} from '../../../../actions/watermark'
import { setPaymentVisible } from '../../../../actions/main'
import crown_golden from '../../../../images/crown_golden.png'
import time_free_icon from '../../../../images/time-free-icon.png'
import { calcCharactersLength } from '../../../../utils/utils'
import { PRESET_ITEM_MAX_CHAR_LENGTH } from '../../../../constants/watermark'
import sensors, { parsePlaceValue } from '../../../../utils/sensors'
import { coordinateByPlainItemsList } from './BatchImportGeoData/lib'
import BatchImportGeoData from './BatchImportGeoData'
import ContactUS from '../../../components/ContactUS'
import { FileExcelOutlined } from '@ant-design/icons'

const TextArea = Input.TextArea

interface BunchAddPresetModalProps {
  visible: boolean
  onCancel: () => void
  index: number
}

const BunchAddPresetModal: React.FC<BunchAddPresetModalProps> = (props) => {
  const { visible, onCancel, index } = props
  const [value, setValue] = useState('') //textarea value
  const [itemLength, setItemLength] = useState(0) //按换行符分割后的条目数
  const [maxItemCharLength, setMaxItemCharLength] = useState(0) //条目最大字数
  const [geoImportVisible, setGeoImportVisible] = useState(false) //批量导入地理数据页面
  const watermark = useAppSelector((state) => state.editor.watermark)
  const config = useAppSelector((state) => state.main.config)
  const itemCnt = useAppSelector((state) => state.group.itemCnt)
  const itemLimit = parseInt(
    itemCnt || config?.normal_group_option_limit || 10000
  )
  const dispatch = useAppDispatch()
  const onChange = (newWatermark) => dispatch(setEditorWatermark(newWatermark))
  const { isVip } = useVip()
  const sensorsRole = useSensorsRole()
  const presetItems = watermark?.items?.[index]?.contentEdit?.itemsList || []

  const plainItemsList = useMemo(
    () => Array.from(new Set(value.split('\n'))),
    [value]
  )

  const geoItemsList = useAppSelector((state) => state.editor.geoItemsList)
  const isItemCharOverLimit = maxItemCharLength > PRESET_ITEM_MAX_CHAR_LENGTH

  useEffect(() => {
    setValue(presetItems.join('\n'))
  }, [visible, presetItems])

  useEffect(() => {
    const items = value.split('\n')
    const maxCharLength = Math.max(
      ...items.map((item) => calcCharactersLength(item))
    )
    setItemLength(items.length)
    setMaxItemCharLength(maxCharLength)
  }, [value])

  const onBunchAddPresetOptions = () => {
    sensors.track('web_click_pop_batch_upload_content', {
      clickItem: 'confirm',
      option_count: Array.from(new Set(value.split('\n'))).length
    })
    const newGeoItemsList = coordinateByPlainItemsList(
      watermark?.id,
      watermark?.items?.[index]?.id,
      geoItemsList,
      plainItemsList
    )
    const newItems = watermark.items.map((it, idx) =>
      idx === index
        ? {
            ...it,
            contentEdit: {
              ...it.contentEdit,
              itemsList: plainItemsList
            }
          }
        : it
    )
    onChange({ ...watermark, items: newItems })
    dispatch(setEditorGeoItemList(newGeoItemsList))
    onCancel()
  }

  const handleBatchImportGeoDataClick = () => {
    sensors.track('web_click_pop_batch_upload_content', {
      clickItem: 'uploadBatch'
    })
    setGeoImportVisible(true)
  }

  const handleClose = () => {
    sensors.track('web_click_pop_batch_upload_content', {
      clickItem: 'cancel'
    })
    onCancel()
  }

  return (
    <>
      <Modal
        title="批量设置选项"
        open={visible}
        getContainer={false}
        onCancel={handleClose}
        width={600}
        styles={{ body: { paddingBottom: '8px' } }}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            取消
          </Button>,
          <Button
            key="submit"
            type="primary"
            disabled={itemLength > itemLimit || isItemCharOverLimit}
            onClick={onBunchAddPresetOptions}
          >
            确认
          </Button>
        ]}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <p>请在输入框中按照格式输入选项，也可以复制后粘贴到输入框中： </p>
          <Button
            size="small"
            type="dashed"
            style={{ background: '#F7F7F7', color: '#0093FF' }}
            onClick={handleBatchImportGeoDataClick}
          >
            批量上传 <FileExcelOutlined style={{ color: '#49A130' }} />
          </Button>
        </div>
        <TextArea
          placeholder="预设选项一&#10;预设选项二&#10;预设选项三"
          autoSize={{ minRows: 8, maxRows: 16 }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}
        >
          <div>
            {/* 某条目超出单条目字数上线 */}
            {isItemCharOverLimit ? (
              <p style={{ margin: '10px 0', color: '#D40000' }}>
                提示：单个选项不能超过{PRESET_ITEM_MAX_CHAR_LENGTH / 2}个汉字
              </p>
            ) : (
              <p style={{ margin: '10px 0' }}>
                {`提示：选项之间用回车换行分割，最多添加${itemLimit}行`}
              </p>
            )}
            <p style={{ margin: 0 }}>
              {`超过${itemLimit}条`}
              <ContactUS
                style={{ display: 'inline' }}
                fromPage="预设选项不限制数量"
                track={() => {
                  sensors.track('web_get_action_pop_watermark_item_pay', {
                    actionType: 'clickContactUs',
                    isGroupVIP: isVip,
                    role: sensorsRole
                  })
                }}
              >
                <a>联系客服</a>
              </ContactUS>
              配置
            </p>
          </div>

          <div
            style={{
              margin: '10px 0',
              color: '#A36B48',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span
              style={
                itemLength > itemLimit
                  ? { color: '#D40000' }
                  : { color: '#646566' }
              }
            >
              {itemLength}
            </span>
            <span>/{itemLimit}</span>
            <div
              style={{
                background: 'linear-gradient(to left, #FFE4BB, #FFF7EB)',
                color: '#9A6516',
                fontSize: 13,
                padding: '0 4px',
                height: 18,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              试用
            </div>
          </div>
        </div>
      </Modal>
      {visible && geoImportVisible && (
        <BatchImportGeoData
          index={index}
          plainItemsList={plainItemsList}
          onClose={() => setGeoImportVisible(false)}
          onFinish={(newPlainItemsList) =>
            setValue(newPlainItemsList.join('\n'))
          }
        />
      )}
    </>
  )
}

export default BunchAddPresetModal
